import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";

import {
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  Typography,
  MenuItem,
  CircularProgress,
  Box,
  Container,
  Divider
} from "@mui/material";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";
import FileUploadIcon from "@mui/icons-material/FileUpload";

import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { applicationSchema } from "../validationSchema";

import { supabase } from "../lib/supabaseClient";

const BUCKET_NAME = "gmobiles";
const TABLE_NAME = "applications";

/* ================= SECTION CARD ================= */

const SectionCard = ({ title, children }) => (
  <Card sx={{ mb: 4 }}>
    <CardContent sx={{ p: 4 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        {title}
      </Typography>

      <Divider sx={{ mb: 3 }} />

      <Grid container spacing={3}>
        {children}
      </Grid>
    </CardContent>
  </Card>
);

/* ================= MAIN COMPONENT ================= */

const ApplicationEdit = () => {

  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [files, setFiles] = useState({});
  const [existingFiles, setExistingFiles] = useState({});

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(applicationSchema)
  });

  /* ================= FILE CHANGE ================= */

  const handleFileChange = (e) => {

    const file = e.target.files[0];

    if (file && file.size > 5 * 1024 * 1024) {
      alert("Max file size 5MB");
      return;
    }

    setFiles(prev => ({
      ...prev,
      [e.target.name]: file
    }));

  };

  /* ================= FETCH DATA ================= */

  useEffect(() => {

    const fetchApplication = async () => {

      const { data, error } = await supabase
        .from(TABLE_NAME)
        .select("*")
        .eq("id", id)
        .single();

      if (error) {
        console.error(error);
        navigate("/");
        return;
      }

      reset({
        name: data.name || "",
        fatherName: data.father_name || "",
        motherName: data.mother_name || "",
        wifeName: data.spouse_name || "",
        mobile: data.mobile || "",
        whatsappMobile: data.whatsapp || "",
        email: data.email || "",
        address: data.address || "",
        birthdate: data.birthdate || "",
        aadharNo: data.aadhar || "",
        panNo: data.pan || "",
        voterId: data.voter_id || "",
        drivingLicense: data.driving_license || "",
        job: data.job || "",
        duration: data.duration || "",
        amount: data.amount || "",
        purchaseType: data.purchase_type || ""
      });

      setExistingFiles({
        file1: data.file1,
        file2: data.file2,
        file3: data.file3,
        file4: data.file4,
        file5: data.file5
      });

      setLoading(false);

    };

    fetchApplication();

  }, [id, reset, navigate]);

  /* ================= UPDATE ================= */

  const onSubmit = async (formData) => {

    setSubmitting(true);

    try {

      const uploaded = { ...existingFiles };

      for (let key of Object.keys(files)) {

        if (files[key]) {

          const fileName = `${Date.now()}-${files[key].name}`;

          const { error: uploadError } = await supabase.storage
            .from(BUCKET_NAME)
            .upload(fileName, files[key]);

          if (uploadError) throw uploadError;

          const { data } = supabase.storage
            .from(BUCKET_NAME)
            .getPublicUrl(fileName);

          uploaded[key] = data.publicUrl;
        }
      }

      const payload = {
        name: formData.name,
        father_name: formData.fatherName,
        mother_name: formData.motherName,
        spouse_name: formData.wifeName,
        mobile: formData.mobile,
        whatsapp: formData.whatsappMobile,
        email: formData.email,
        address: formData.address,
        birthdate: formData.birthdate,
        aadhar: formData.aadharNo,
        pan: formData.panNo,
        voter_id: formData.voterId,
        driving_license: formData.drivingLicense,
        job: formData.job,
        duration: formData.duration,
        amount: formData.amount,
        purchase_type: formData.purchaseType,
        file1: uploaded.file1,
        file2: uploaded.file2,
        file3: uploaded.file3,
        file4: uploaded.file4,
        file5: uploaded.file5
      };

      const { error } = await supabase
        .from(TABLE_NAME)
        .update(payload)
        .eq("id", id);

      if (error) throw error;

      alert("Application updated successfully");

      navigate("/");

    } catch (err) {

      console.error(err);
      alert("Update failed: " + err.message);

    }

    setSubmitting(false);

  };

  if (loading) {
    return (
      <Box sx={{ textAlign: "center", py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (

    <Box sx={{ backgroundColor: "#F5F7FA", minHeight: "100vh", py: 6 }}>

      <Container maxWidth="md">

        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/")}
          sx={{ mb: 3 }}
        >
          Back
        </Button>

        <Typography variant="h4" sx={{ mb: 4 }}>
          Edit Application
        </Typography>

        <form onSubmit={handleSubmit(onSubmit)}>

          {/* PERSONAL DETAILS */}

          <SectionCard title="Personal Details">

            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Name" {...register("name")}
                error={!!errors.name} helperText={errors.name?.message}/>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Mobile" {...register("mobile")}
                error={!!errors.mobile} helperText={errors.mobile?.message}/>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Father Name" {...register("fatherName")} />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Mother Name" {...register("motherName")} />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Spouse Name" {...register("wifeName")} />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField fullWidth label="WhatsApp" {...register("whatsappMobile")} />
            </Grid>

            <Grid item xs={12}>
              <TextField fullWidth label="Address" {...register("address")} />
            </Grid>

          </SectionCard>

          {/* IDENTIFICATION */}

          <SectionCard title="Identification">

            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Aadhar" {...register("aadharNo")} />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField fullWidth label="PAN" {...register("panNo")} />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Voter ID" {...register("voterId")} />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Driving License" {...register("drivingLicense")} />
            </Grid>

          </SectionCard>

          {/* FINANCE */}

          <SectionCard title="Employment & Finance">

            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Job" {...register("job")} />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Duration" {...register("duration")} />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField select fullWidth label="Purchase Type" {...register("purchaseType")}>
                <MenuItem value="">Select</MenuItem>
                <MenuItem value="cash">Cash</MenuItem>
                <MenuItem value="tvs">TVS</MenuItem>
                <MenuItem value="bajaj">Bajaj</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Amount" type="number" {...register("amount")} />
            </Grid>

          </SectionCard>

          {/* FILES */}

          <SectionCard title="Upload Files">

            {[1,2,3,4,5].map(num => (

              <Grid item xs={12} md={6} key={num}>

                {existingFiles[`file${num}`] && (
                  <Box sx={{ mb: 1 }}>
                    <a href={existingFiles[`file${num}`]} target="_blank">
                      View Existing File {num}
                    </a>
                  </Box>
                )}

                <Button
                  component="label"
                  fullWidth
                  variant="outlined"
                  startIcon={<FileUploadIcon />}
                >
                  {files[`file${num}`]?.name || `Replace File ${num}`}
                  <input hidden type="file" name={`file${num}`} onChange={handleFileChange}/>
                </Button>

              </Grid>

            ))}

          </SectionCard>

          <Box textAlign="center" mt={4}>

            <Button
              type="submit"
              variant="contained"
              startIcon={submitting ? <CircularProgress size={20}/> : <SaveIcon />}
              disabled={submitting}
            >
              {submitting ? "Updating..." : "Update"}
            </Button>

          </Box>

        </form>

      </Container>

    </Box>

  );

};

export default ApplicationEdit;
